package com.patronat.top.features.venues.presentation

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.patronat.top.dto.VenueResponse
import com.patronat.top.ui.atoms.AppButton
import com.patronat.top.ui.atoms.AppRadioButton
import com.patronat.top.ui.atoms.AppSwitch
import com.patronat.top.ui.atoms.ButtonType
import com.patronat.top.ui.models.SelectOption
import com.patronat.top.ui.molecules.inputs.AppInput
import com.patronat.top.ui.molecules.inputs.AppSelectField
import com.patronat.top.ui.molecules.inputs.SearchInput
import com.patronat.top.ui.theme.BodyLarge
import com.patronat.top.ui.theme.MineShaft900
import com.patronat.top.ui.theme.VenueSectionTitleText
import com.patronat.top.ui.theme.White
import org.jetbrains.compose.resources.stringResource
import top.features.venues.generated.resources.Res
import top.features.venues.generated.resources.venue_add_dialog_cancel_button
import top.features.venues.generated.resources.venue_add_dialog_ai
import top.features.venues.generated.resources.venue_add_dialog_ai_create_button
import top.features.venues.generated.resources.venue_add_dialog_ai_create_immediately
import top.features.venues.generated.resources.venue_add_dialog_ai_prefill_button
import top.features.venues.generated.resources.venue_add_dialog_ai_url_label
import top.features.venues.generated.resources.venue_add_dialog_ai_url_placeholder
import top.features.venues.generated.resources.venue_add_dialog_create_button
import top.features.venues.generated.resources.venue_add_dialog_empty
import top.features.venues.generated.resources.venue_add_dialog_manager_question
import top.features.venues.generated.resources.venue_add_dialog_manual
import top.features.venues.generated.resources.venue_add_dialog_name_label
import top.features.venues.generated.resources.venue_add_dialog_name_placeholder
import top.features.venues.generated.resources.venue_add_dialog_no
import top.features.venues.generated.resources.venue_add_dialog_question
import top.features.venues.generated.resources.venue_add_dialog_request_button
import top.features.venues.generated.resources.venue_add_dialog_search_placeholder
import top.features.venues.generated.resources.venue_add_dialog_title
import top.features.venues.generated.resources.venue_add_dialog_yes

@Composable
fun AddVenueActionDialog(
    visible: Boolean,
    mode: AddVenueActionMode,
    query: String,
    candidates: List<VenueResponse>,
    selectedCandidateId: String?,
    isLoading: Boolean,
    canUseAiImport: Boolean = false,
    aiWebsiteUrl: String = "",
    aiCreateImmediately: Boolean = false,
    onDismiss: () -> Unit,
    onModeChanged: (AddVenueActionMode) -> Unit,
    onQueryChanged: (String) -> Unit,
    onCandidateSelected: (String) -> Unit,
    onAiWebsiteUrlChanged: (String) -> Unit = {},
    onAiCreateImmediatelyChanged: (Boolean) -> Unit = {},
    onConfirm: () -> Unit
) {
    if (!visible) return

    val title = stringResource(Res.string.venue_add_dialog_title)
    val question = stringResource(Res.string.venue_add_dialog_question)
    val yesLabel = stringResource(Res.string.venue_add_dialog_yes)
    val noLabel = stringResource(Res.string.venue_add_dialog_no)
    val nameLabel = stringResource(Res.string.venue_add_dialog_name_label)
    val namePlaceholder = stringResource(Res.string.venue_add_dialog_name_placeholder)
    val searchPlaceholder = stringResource(Res.string.venue_add_dialog_search_placeholder)
    val createButton = stringResource(Res.string.venue_add_dialog_create_button)
    val requestButton = stringResource(Res.string.venue_add_dialog_request_button)
    val cancelButton = stringResource(Res.string.venue_add_dialog_cancel_button)
    val emptyText = stringResource(Res.string.venue_add_dialog_empty)
    val managerQuestion = stringResource(Res.string.venue_add_dialog_manager_question)
    val manualLabel = stringResource(Res.string.venue_add_dialog_manual)
    val aiLabel = stringResource(Res.string.venue_add_dialog_ai)
    val aiUrlLabel = stringResource(Res.string.venue_add_dialog_ai_url_label)
    val aiUrlPlaceholder = stringResource(Res.string.venue_add_dialog_ai_url_placeholder)
    val aiCreateImmediatelyLabel = stringResource(Res.string.venue_add_dialog_ai_create_immediately)
    val aiPrefillButton = stringResource(Res.string.venue_add_dialog_ai_prefill_button)
    val aiCreateButton = stringResource(Res.string.venue_add_dialog_ai_create_button)

    val options = candidates.map { venue ->
        SelectOption(id = venue.id, label = venue.name)
    }
    val selectedOption = options.firstOrNull { it.id == selectedCandidateId }
    val isRequestMode = mode == AddVenueActionMode.REQUEST_ACCESS
    val isAiMode = mode == AddVenueActionMode.AI_IMPORT
    val confirmEnabled = when {
        isAiMode -> aiWebsiteUrl.isNotBlank()
        isRequestMode -> selectedCandidateId != null
        else -> true
    }
    val confirmText = when {
        isAiMode && aiCreateImmediately -> aiCreateButton
        isAiMode -> aiPrefillButton
        isRequestMode -> requestButton
        else -> createButton
    }

    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(
            usePlatformDefaultWidth = false,
            dismissOnBackPress = true,
            dismissOnClickOutside = true
        )
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(MineShaft900.copy(alpha = 0.35f)),
            contentAlignment = Alignment.Center
        ) {
            Column(
                modifier = Modifier
                    .widthIn(max = 720.dp)
                    .fillMaxWidth()
                    .background(White, RoundedCornerShape(16.dp))
                    .padding(24.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Text(
                    text = title,
                    style = VenueSectionTitleText,
                    color = MineShaft900
                )

                Text(
                    text = if (canUseAiImport) managerQuestion else question,
                    style = BodyLarge,
                    color = MineShaft900
                )

                Row(
                    horizontalArrangement = Arrangement.spacedBy(16.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    if (canUseAiImport) {
                        AppRadioButton(
                            selected = !isAiMode,
                            onClick = { onModeChanged(AddVenueActionMode.CREATE_NEW) },
                            label = manualLabel
                        )
                        AppRadioButton(
                            selected = isAiMode,
                            onClick = { onModeChanged(AddVenueActionMode.AI_IMPORT) },
                            label = aiLabel
                        )
                    } else {
                        AppRadioButton(
                            selected = isRequestMode,
                            onClick = { onModeChanged(AddVenueActionMode.REQUEST_ACCESS) },
                            label = yesLabel
                        )
                        AppRadioButton(
                            selected = !isRequestMode,
                            onClick = { onModeChanged(AddVenueActionMode.CREATE_NEW) },
                            label = noLabel
                        )
                    }
                }

                if (isAiMode) {
                    AppInput(
                        value = aiWebsiteUrl,
                        onValueChange = onAiWebsiteUrlChanged,
                        label = aiUrlLabel,
                        placeholder = aiUrlPlaceholder,
                        enabled = !isLoading,
                        modifier = Modifier.fillMaxWidth()
                    )

                    Row(
                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        AppSwitch(
                            checked = aiCreateImmediately,
                            onCheckedChange = onAiCreateImmediatelyChanged,
                            enabled = !isLoading
                        )
                        Text(
                            text = aiCreateImmediatelyLabel,
                            style = BodyLarge,
                            color = MineShaft900
                        )
                    }
                } else if (isRequestMode) {
                    SearchInput(
                        query = query,
                        onQueryChange = onQueryChanged,
                        placeholder = searchPlaceholder,
                        modifier = Modifier.fillMaxWidth()
                    )

                    AppSelectField(
                        selectedOption = selectedOption,
                        options = options,
                        onOptionSelected = { selected -> onCandidateSelected(selected.id) },
                        label = nameLabel,
                        placeholder = namePlaceholder,
                        emptyResultsText = if (isLoading) "" else emptyText,
                        modifier = Modifier.fillMaxWidth()
                    )
                }

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.End
                ) {
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        AppButton(
                            text = cancelButton,
                            onClick = onDismiss,
                            type = ButtonType.BrownOutline
                        )
                        AppButton(
                            text = confirmText,
                            onClick = onConfirm,
                            enabled = confirmEnabled && (isAiMode || !isLoading),
                            isLoading = isAiMode && isLoading
                        )
                    }
                }
            }
        }
    }
}
